/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { EnvironmentVariableNameValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  FORM_THEME_LITE,
  Form,
  ICON_ADD_ROUND,
  ICON_DELETE_FOREVER,
  TOAST_TYPE_SUCCESS,
  TextField,
  TextFieldProps,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { ChangeEvent, createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  JobRunnerServiceCreateContextDto,
  JobRunnerServiceDeleteContextDto,
  JobRunnerServiceUpdateContextDto,
  getJobRunnerService,
} from '../../../../Services/job-runner';
import {
  ENVIRONMENT_VARIABLE_DATA_IDS,
  ENVIRONMENT_VARIABLE_FORM_IDS,
  JOB_CONTEXT_FORM_DATA_IDS,
  JOB_CONTEXT_FORM_IDS,
} from './JobContextForm.constants';
import { JobContextFormProps } from './JobContextForm.props';

/**
 * The form for creating or updating a job context.
 */
export const JobContextForm = (props: JobContextFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { jobContext, clearCache, setButtonKeys, setButtons, close, setAfterClose } = props;

  // Job Runner service
  const jobRunnerService = getJobRunnerService();

  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [contextNameError, setContextNameError] = useState<Record<string, string | null>>({});
  const [environmentFieldErrors, setEnvironmentFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);

  // Default context values if not pre-populated
  let contextDescription = '';
  let contextEnvironment: Record<string, string> = {};
  let contextName = '';

  if (jobContext) {
    contextDescription = jobContext.description;
    contextEnvironment = jobContext.environment;
    contextName = jobContext.contextName;
  }

  const variableMap = new Map(Object.entries(contextEnvironment));
  // A map is used to maintain the order when adding a new variable
  const [envVarInputs, setEnvVarInputs] = useState<Map<string, string>>(variableMap);
  const [showAddNewVariable, setShowAddNewVariable] = useState<boolean>(false);

  const contextNameRef = useRef<HTMLInputElement>(null);
  const contextDescriptionRef = useRef<HTMLInputElement>(null);

  const { updateActiveToast } = useToastStore();

  // Labels
  const labels = useMemo(() => {
    return {
      add: t(CONFIGURATION_STRINGS.add),
      addVariable: t(CONFIGURATION_STRINGS.addVariable),
      cancel: t(CONFIGURATION_STRINGS.cancel),
      changesSaved: t(CONFIGURATION_STRINGS.changesSaved),
      contextSuccessToastDescription1: t(CONFIGURATION_STRINGS.contextSuccessToastDescription1),
      contextSuccessToastDescription2: t(CONFIGURATION_STRINGS.contextSuccessToastDescription2),
      createContext: t(CONFIGURATION_STRINGS.createContext),
      createdContextTitle: t(CONFIGURATION_STRINGS.createdContextTitle),
      description: t(CONFIGURATION_STRINGS.description),
      duplicateEnvVarName: t(CONFIGURATION_STRINGS.duplicateEnvVarName),
      enterDescription: t(CONFIGURATION_STRINGS.enterDescription),
      enterName: t(CONFIGURATION_STRINGS.enterName),
      enterValue: t(CONFIGURATION_STRINGS.enterValue),
      environmentVariables: t(CONFIGURATION_STRINGS.environmentVariables),
      errorUnknown: t(CONFIGURATION_STRINGS.errorUnknown),
      helperTextForContextName: t(CONFIGURATION_STRINGS.helperTextForContextName),
      invalidEnvVarName: t(CONFIGURATION_STRINGS.invalidEnvVarName),
      nameAlreadyInUseError: t(CONFIGURATION_STRINGS.nameAlreadyInUseError),
      nameRequired: t(CONFIGURATION_STRINGS.nameRequired),
      required: t(CONFIGURATION_STRINGS.requiredField),
      saveChanges: t(CONFIGURATION_STRINGS.saveChanges),
      variableName: t(CONFIGURATION_STRINGS.variableName),
    };
  }, [t]);

  const customClose = useCallback(() => {
    // Refresh user list on dialog close
    clearCache(new Date().toISOString());
  }, [clearCache]);

  // Form input refs
  const newEnvVarRefName = useRef<HTMLInputElement>(null);
  const newEnvVarNameRefProps = {
    errorMessage: environmentFieldErrors[ENVIRONMENT_VARIABLE_FORM_IDS.new] || null,
    fieldLabel: labels.variableName,
    id: ENVIRONMENT_VARIABLE_FORM_IDS.new,
    name: 'new-variable',
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
  };

  const envVarRefs = useMemo(
    () => Array.from({ length: envVarInputs.size }).map(() => createRef<HTMLInputElement>()),
    [envVarInputs.size],
  );

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const validEnvVarName = useMemo(() => {
    // istanbul ignore next
    return new EnvironmentVariableNameValidator<boolean>((): boolean => false);
  }, []);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...contextNameError };
      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;

        // Check that the input fields are valid
        if (!notEmptyValidator.validate(inputValue)) {
          nextErrors[inputFieldId] = labels.required;
        } else {
          delete nextErrors[inputFieldId];
        }
      });
      setContextNameError(nextErrors);
    },
    [contextNameError, labels.required, notEmptyValidator],
  );

  const validateEnvVarNameField = useCallback(
    (input: HTMLInputElement): void => {
      const error = labels.invalidEnvVarName;
      const nextErrors = { ...environmentFieldErrors };
      const inputFieldId = input.getAttribute(`id`) as string;
      // istanbul ignore next
      if (!validEnvVarName.doValidate(input.value)) {
        nextErrors[inputFieldId] = error;
      } else {
        // istanbul ignore next
        delete nextErrors[inputFieldId];
      }
      setEnvironmentFieldErrors(nextErrors);
    },
    [environmentFieldErrors, labels, validEnvVarName],
  );

  const checkForExistingEnvVar = useCallback(
    (input: HTMLInputElement): boolean => {
      if (envVarInputs.has(input.value)) {
        const error = labels.duplicateEnvVarName;
        const nextErrors = { ...environmentFieldErrors };
        const inputFieldId = input.getAttribute(`id`) as string;
        nextErrors[inputFieldId] = error;
        setEnvironmentFieldErrors(nextErrors);
        return true;
      } else {
        return false;
      }
    },
    [environmentFieldErrors, envVarInputs, labels.duplicateEnvVarName],
  );

  const handleShowAddInput = useCallback(() => {
    const nextErrors = { ...environmentFieldErrors };
    nextErrors[ENVIRONMENT_VARIABLE_FORM_IDS.new] = null;
    setEnvironmentFieldErrors(nextErrors);
    setShowAddNewVariable(true);
  }, [environmentFieldErrors]);

  const handleAddInput = useCallback(() => {
    const input = newEnvVarRefName.current as HTMLInputElement;

    if (!notEmptyValidator.validate(input.value)) {
      validateFields([input]);
    }

    if (checkForExistingEnvVar(input)) {
      return;
    }

    if (!validEnvVarName.doValidate(input.value)) {
      validateEnvVarNameField(input);
    } else {
      // We are using a map to preserve order.
      // istanbul ignore next
      const nameValue = newEnvVarRefName.current?.value as string;
      envVarInputs.set(nameValue, '');
      setShowAddNewVariable(false);
    }
  }, [
    checkForExistingEnvVar,
    envVarInputs,
    notEmptyValidator,
    validateEnvVarNameField,
    validateFields,
    validEnvVarName,
  ]);

  // Delete function triggers re-render by creating a new map
  const deleteEnvironmentVariable = (variableName: string): void => {
    const updatedEnvVarInputs = new Map(envVarInputs);
    updatedEnvVarInputs.delete(variableName);
    setEnvVarInputs(updatedEnvVarInputs);
  };
  const createTextFieldPropForEnvVar = (envVarKey: string): TextFieldProps => {
    return {
      errorMessage: environmentFieldErrors[ENVIRONMENT_VARIABLE_FORM_IDS.value + envVarKey] || null,
      fieldLabel: envVarKey,
      id: ENVIRONMENT_VARIABLE_FORM_IDS.value + envVarKey,
      name: envVarKey,
    };
  };

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );
  const getUpdatedEnvVars = useCallback((): Record<string, string> => {
    const updatedEnvVars = envVarRefs.reduce((acc, input) => {
      // istanbul ignore next
      const varName = input.current?.name as string;
      // istanbul ignore next
      return {
        // istanbul ignore next
        ...acc,
        // istanbul ignore next
        [varName]: input?.current?.value,
      };
    }, {});
    return updatedEnvVars;
  }, [envVarRefs]);

  const baseSuccessToastProps: ToastProps = useMemo(() => {
    return {
      showToast: true,
      stateHandler: useToastStore,
      toastType: TOAST_TYPE_SUCCESS,
    };
  }, []);

  const createContext = useCallback(
    async (name: string, description: string, environment: Record<string, string>) => {
      const dto: JobRunnerServiceCreateContextDto = {
        contextName: name,
        description,
        environment,
      };

      try {
        await lastValueFrom(jobRunnerService.createJobContext(dto));
      } catch (err) {
        /** Do not set formError if Bad Request. We handle with it inline error component. */
        if ((err as AjaxError).status === 400) {
          const nextErrors = { ...contextNameError };
          nextErrors['context-name'] = labels.nameAlreadyInUseError;
          setContextNameError(nextErrors);
        }

        throw err;
      }
    },
    [contextNameError, jobRunnerService, labels.nameAlreadyInUseError],
  );

  const deleteContext = useCallback(
    async (name: string): Promise<void> => {
      const jobContextDto: JobRunnerServiceDeleteContextDto = { contextName: name };
      await lastValueFrom(jobRunnerService.deleteJobContext(jobContextDto));
    },
    [jobRunnerService],
  );

  const updateContext = useCallback(
    async (name: string, description: string, environment: Record<string, string>, isNameChanged: boolean) => {
      if (isNameChanged) {
        await createContext(name, description, environment);
        await deleteContext(contextName);
      } else {
        const dto: JobRunnerServiceUpdateContextDto = {
          contextName: name,
          description,
          environment,
        };

        await lastValueFrom(jobRunnerService.updateJobContext(dto));
      }
    },
    [contextName, createContext, deleteContext, jobRunnerService],
  );

  const submitHandler = useCallback(async () => {
    const inputs = {
      inputDescription: contextDescriptionRef.current as HTMLInputElement,
      inputName: contextNameRef.current as HTMLInputElement,
    };

    // Validate the required input fields
    if (!notEmptyValidator.validate(inputs.inputName.value)) {
      validateFields([inputs.inputName]);
      setSubmit(false);
      return;
    }

    // Reset any form errors
    setFormError(``);

    // Prepare the parameters required for the job context requests
    const name = inputs.inputName.value.trim();
    const description = inputs.inputDescription.value.trim();
    const environment = getUpdatedEnvVars();

    try {
      if (jobContext) {
        const isNameChanged = contextName !== name;
        await updateContext(name, description, environment, isNameChanged);
      } else {
        await createContext(name, description, environment);
      }

      close();
      updateActiveToast(null);

      // Set up toast (for successful update by default)
      const successToastProps: ToastProps = {
        ...baseSuccessToastProps,
        title: labels.changesSaved,
      };

      if (!jobContext) {
        // Modify toast for successful creation
        successToastProps.title = labels.createdContextTitle;
        successToastProps.toastContent = (
          <span>
            {labels.contextSuccessToastDescription1}
            <i>{name}</i>
            {labels.contextSuccessToastDescription2}
          </span>
        );
      }

      updateActiveToast(<Toast {...successToastProps} />);
      return;
    } catch (err) {
      if ((err as AjaxError).status !== 400) {
        setFormError(labels.errorUnknown);
      }

      setSubmit(false);
    }
  }, [
    baseSuccessToastProps,
    close,
    contextName,
    createContext,
    getUpdatedEnvVars,
    jobContext,
    labels,
    notEmptyValidator,
    updateActiveToast,
    updateContext,
    validateFields,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        dataId={JOB_CONTEXT_FORM_DATA_IDS.createContextCancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const createButton = (
      <Button
        dataId={JOB_CONTEXT_FORM_DATA_IDS.createContextConfirm}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={!jobContext ? labels.createContext : labels.saveChanges}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    // Check for form submission
    if (isFormSubmitted) {
      setAfterClose && setAfterClose(() => customClose);
      setButtonKeys([`create`]);
      setButtons([createButton]);
      submitHandler();
    } else {
      setButtonKeys([`cancel`, `create`]);
      setButtons([cancelButton, createButton]);
    }
  }, [
    close,
    customClose,
    isFormSubmitted,
    jobContext,
    labels,
    setAfterClose,
    setButtonKeys,
    setButtons,
    submitHandler,
  ]);

  // Context Name
  const contextNameFieldProps: TextFieldProps = {
    errorMessage: contextNameError[JOB_CONTEXT_FORM_IDS.name] || null,
    fieldLabel: labels.nameRequired,
    id: JOB_CONTEXT_FORM_IDS.name,
    name: JOB_CONTEXT_FORM_IDS.name,
    note: labels.helperTextForContextName,
    onChange: () => {
      setContextNameError({});
    },
    placeholder: labels.enterName,
    value: contextName,
  };

  // Context Description
  const contextDescriptionFieldProps: TextFieldProps = {
    classes: `job-context-description`,
    fieldLabel: labels.description,
    id: JOB_CONTEXT_FORM_IDS.description,
    name: JOB_CONTEXT_FORM_IDS.description,
    placeholder: labels.enterDescription,
    value: contextDescription,
  };

  return (
    <div className="job-context-dialog">
      <Form
        classes="job-context-form"
        data-id={JOB_CONTEXT_FORM_IDS.form}
        errorMessage={formError}
        legend={''}
        theme={FORM_THEME_LITE}
      >
        <TextField {...contextNameFieldProps} data-id={JOB_CONTEXT_FORM_IDS.name} ref={contextNameRef} />
        <TextField
          {...contextDescriptionFieldProps}
          data-id={JOB_CONTEXT_FORM_IDS.description}
          ref={contextDescriptionRef}
        />
        <span className="env-var-dialog__header">{labels.environmentVariables}</span>
        {/* Map the current sets environment variables to the text field where the name
         of the variable is the field name and the value is set to the current value of the input */}
        {Array.from(envVarInputs).map(([key, value], index) => (
          <div className="env-var-dialog__form-control-container" key={key}>
            <div className="env-var-dialog__form-control-env-var">
              <TextField
                {...createTextFieldPropForEnvVar(key)}
                classes="env-var-dialog__form-control"
                data-id={ENVIRONMENT_VARIABLE_FORM_IDS.value}
                ref={envVarRefs[index]}
                value={value}
              />
            </div>
            <Button
              classes="env-var-dialog__form-trash-icon"
              data-id={ENVIRONMENT_VARIABLE_DATA_IDS.delete}
              format={BUTTON_FORMAT_TRANSPARENT}
              icon={ICON_DELETE_FOREVER}
              onClick={(): void => deleteEnvironmentVariable(key)}
              theme={BUTTON_THEME_DEFAULT}
            />
          </div>
        ))}
        {/* Button to show the add new variable div below */}
        <div>
          {showAddNewVariable === false && (
            <Button
              classes="env-var-dialog__add-var-button"
              format={BUTTON_FORMAT_TRANSPARENT}
              icon={ICON_ADD_ROUND}
              onClick={(): void => handleShowAddInput()}
              text={labels.addVariable}
              theme={BUTTON_THEME_DEFAULT}
            />
          )}
        </div>
        {/* The add new variable block */}
        {showAddNewVariable === true && (
          <div className="env-var-dialog__new-env-var-form">
            <TextField {...newEnvVarNameRefProps} ref={newEnvVarRefName} />
            <div className="env-var-dialog__new-env-var-form-buttons">
              <Button
                data-id={ENVIRONMENT_VARIABLE_DATA_IDS.cancelNewVariable}
                format={BUTTON_FORMAT_SOFT}
                onClick={(): void => setShowAddNewVariable(false)}
                text={labels.cancel}
                theme={BUTTON_THEME_DEFAULT}
              />
              <Button
                format={BUTTON_FORMAT_STRONG}
                onClick={handleAddInput}
                text={labels.add}
                theme={BUTTON_THEME_PRIMARY}
              />
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};
